import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("App crashed:", error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, errorInfo } = this.state;

    if (!error) return this.props.children;

    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="w-full max-w-2xl rounded-lg border border-border bg-card p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-base font-medium tracking-tight">Preview couldn’t load</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                The app hit a runtime error. Reload the preview; if it persists, share the error details below.
              </p>
            </div>
            <Button variant="outline" onClick={this.handleReload}>
              Reload
            </Button>
          </div>

          <div className="mt-4 rounded-md border border-border bg-muted/40 p-3">
            <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Error</p>
            <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-foreground">
              {error.message || String(error)}
            </pre>
            {errorInfo?.componentStack && (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs text-muted-foreground">Component stack</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">
                  {errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  }
}
